import { Select } from "antd"
import { FC, memo, useCallback, useMemo } from "react"
import linkIcon from "assets/svg/Link.svg"
import { Button as CustomButton } from "components"
import StepActionBar from "./upload-timesheet/StepActionBar"

const DROPDOWN_SUFFIX_ICON = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth="1.5"
    stroke="currentColor"
    className="w-4 h-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m19.5 8.25-7.5 7.5-7.5-7.5"
    />
  </svg>
)

// ─── Types ───────────────────────────────────────────────────────────────────

interface MapField {
  stateKey: string
  title: string
}

interface MapColumnProps {
  onImport: () => void
  isLoading: boolean
  isMapComplete: boolean
  hasInvalid: boolean
  requiredFields: MapField[]
  optionalFields?: MapField[]
  columnMap: Record<string, string | null>
  invalidMap: Record<string, string | null>
  optionsFor: (stateKey: string) => string[]
  onColumnMapChange: (stateKey: string, value: string | null) => void
  description?: string
}

// ─── Sub Components ───────────────────────────────────────────────────────────

interface MapColumnFooterProps {
  isLoading: boolean
  isMapComplete: boolean
  hasInvalid: boolean
  handleNextStep: () => void
}

const MapColumnFooter: FC<MapColumnFooterProps> = ({
  isLoading,
  isMapComplete,
  hasInvalid,
  handleNextStep,
}) => (
  <StepActionBar>
    <CustomButton
      color="primary"
      className="font-semibold"
      onClick={handleNextStep}
      disabled={isLoading || !isMapComplete || hasInvalid}
      // @ts-ignore
      submitting={isLoading}
    >
      Next step
    </CustomButton>
  </StepActionBar>
)

const SectionLabel: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-6">
    <p className="text-sm font-semibold text-slate-500 mb-2">{children}</p>
    <div className="h-px bg-slate-200" />
  </div>
)

interface MapColumnCardProps {
  title: string
  stateKey: string
  options: string[]
  value?: string | null
  onColumnMapChange: (stateKey: string, value: string | null) => void
  error?: string | null
  withDivider?: boolean
}

const MapColumnCardImpl = ({
  title,
  stateKey,
  options,
  value,
  onColumnMapChange,
  error,
  withDivider,
}: MapColumnCardProps) => {
  const selectOptions = useMemo(
    () => options.map((o) => ({ label: o, value: o })),
    [options]
  )

  const handleChange = useCallback(
    (val: string | null) => onColumnMapChange(stateKey, val ?? null),
    [stateKey, onColumnMapChange]
  )

  return (
    <div
      className={`flex flex-col gap-4 h-max ${
        withDivider ? "border-r border-r-slate-300 pr-3" : ""
      }`}
    >
      <p className="font-bold text-sm text-slate-900">{title}</p>
      <div className="flex items-center">
        <div className="h-px flex-1 bg-slate-200" />
        <div className="border w-6 h-6 flex items-center justify-center rounded-md border-slate-200">
          <img
            src={linkIcon}
            width={15}
            height={15}
            alt="link"
          />
        </div>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <div>
        <p className="text-sm text-slate-400 mb-1">CSV column</p>
        <div className={`${error ? "border border-red-500 rounded" : ""}`}>
          <Select
            placeholder="Select csv column"
            style={{ width: "100%" }}
            suffixIcon={DROPDOWN_SUFFIX_ICON}
            value={value}
            onChange={handleChange}
            options={selectOptions}
            className="[&_*]:!font-circular [&_.ant-select-selection-item]:font-bold text-neutral-500"
          />
        </div>
        {error && <div className="text-red-500 text-xs mt-1">{error}</div>}
      </div>
    </div>
  )
}

const areOptionsEqual = (a: string[], b: string[]) => {
  if (a === b) return true
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}

const MapColumnCard = memo(MapColumnCardImpl, (prev, next) => {
  return (
    prev.title === next.title &&
    prev.stateKey === next.stateKey &&
    prev.value === next.value &&
    prev.error === next.error &&
    prev.withDivider === next.withDivider &&
    prev.onColumnMapChange === next.onColumnMapChange &&
    areOptionsEqual(prev.options, next.options)
  )
})

// ─── Main Component ───────────────────────────────────────────────────────────

const MapColumn: FC<MapColumnProps> = ({
  onImport,
  isLoading,
  isMapComplete,
  hasInvalid,
  requiredFields,
  optionalFields = [],
  columnMap,
  invalidMap,
  optionsFor,
  onColumnMapChange,
  description = "Map the column in your csv to our required fields. Please select the field that most accurately describes the type of data you're importing.",
}) => {
  const renderField = useCallback(
    (field: MapField, idx: number, total: number) => (
      <MapColumnCard
        key={field.stateKey}
        title={field.title}
        stateKey={field.stateKey}
        options={optionsFor(field.stateKey)}
        value={columnMap[field.stateKey] ?? null}
        onColumnMapChange={onColumnMapChange}
        error={invalidMap[field.stateKey]}
        withDivider={idx < total - 1}
      />
    ),
    [optionsFor, columnMap, onColumnMapChange, invalidMap]
  )

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-slate-900 mb-1">Map columns</h2>
      <p className="text-sm pb-4">{description}</p>

      <SectionLabel>Required fields</SectionLabel>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        {requiredFields.map((field, idx) =>
          renderField(field, idx, requiredFields.length)
        )}
      </div>

      {optionalFields.length > 0 && (
        <>
          <SectionLabel>Optional fields</SectionLabel>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            {optionalFields.map((field, idx) =>
              renderField(field, idx, optionalFields.length)
            )}
          </div>
        </>
      )}

      <div className="pb-24" />

      <MapColumnFooter
        isLoading={isLoading}
        isMapComplete={isMapComplete}
        hasInvalid={hasInvalid}
        handleNextStep={onImport}
      />
    </div>
  )
}

export default MapColumn
